import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Eye, Handshake, Plus } from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import { ModalDeConvenio } from './ModalDeConvenio';
import { Etiqueta } from './Etiqueta';
import { PanelDelConvenio } from './PanelDelConvenio';
import { enDolares, enFecha, type Convenio, type Resumen, type Situacion } from './contrato';

const FILTROS: { valor: Situacion | ''; rotulo: string }[] = [
  { valor: '', rotulo: 'Todos' },
  { valor: 'POR_APROBAR', rotulo: 'Por aprobar' },
  { valor: 'ATRASADO', rotulo: 'Atrasados' },
  { valor: 'AL_DIA', rotulo: 'Al día' },
  { valor: 'CANCELADO', rotulo: 'Cancelados' },
  { valor: 'INCUMPLIDO', rotulo: 'Incumplidos' },
  { valor: 'ANULADO', rotulo: 'Anulados' },
  { valor: 'RECHAZADO', rotulo: 'Rechazados' },
];

export function PaginaConvenios() {
  const { puede } = useSesion();
  const [situacion, setSituacion] = useState<Situacion | ''>('');
  const [texto, setTexto] = useState('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [abierto, setAbierto] = useState<number | null>(null);
  const [registrando, setRegistrando] = useState(false);
  const buscado = useValorDiferido(texto.trim());

  const resumen = useQuery({
    queryKey: ['convenios', 'resumen'],
    queryFn: () => api.get<Resumen>('/convenios/resumen'),
  });

  const convenios = useQuery({
    queryKey: ['convenios', 'lista', situacion, buscado, pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<Convenio>>('/convenios', {
        query: { situacion, texto: buscado, pagina, tamanio },
      }),
    placeholderData: (anterior) => anterior,
  });

  const columnas: Columna<Convenio>[] = [
    {
      clave: 'contribuyente',
      encabezado: 'Contribuyente',
      contenido: (c) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs font-semibold uppercase text-armazon-800">
            {c.cliente}
          </span>
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="importe text-[10px] text-armazon-400">{c.identificacion}</span>
            <span className="text-[10px] uppercase text-armazon-400">· {c.servicioNombre}</span>
            {c.numero && (
              <span className="importe text-[10px] font-semibold text-marca-700">· {c.numero}</span>
            )}
            {c.migrado && (
              <span className="rounded bg-armazon-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-armazon-500">
                Del libro · Nº {c.numeroEnElLibro}
              </span>
            )}
          </span>
        </span>
      ),
    },
    {
      clave: 'deuda',
      encabezado: 'Convenido',
      alinear: 'derecha',
      contenido: (c) => (
        <span className="block">
          <span className="importe block text-xs text-armazon-700">{enDolares(c.deuda)}</span>
          <span className="importe block text-[10px] text-armazon-400">
            Entrada {enDolares(c.entrada)}
          </span>
        </span>
      ),
    },
    {
      clave: 'pendiente',
      encabezado: 'Pendiente',
      alinear: 'derecha',
      contenido: (c) => (
        <span className="importe text-xs font-bold text-armazon-900">{enDolares(c.pendiente)}</span>
      ),
    },
    {
      clave: 'cuotas',
      encabezado: 'Cuotas',
      alinear: 'centro',
      contenido: (c) => (
        <span className="block text-center">
          <span className="importe block text-xs uppercase text-armazon-700">
            {c.cuotasPendientes} de {c.plazo}
          </span>
          {c.cuotasVencidas > 0 && (
            <span className="importe block text-[10px] font-semibold uppercase text-amber-700">
              {c.cuotasVencidas} vencidas · {enDolares(c.vencido)}
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'proxima',
      encabezado: 'Próxima cuota',
      contenido: (c) =>
        c.proximoVencimiento ? (
          <span className="block">
            <span className="importe block text-xs text-armazon-700">
              {enFecha(c.proximoVencimiento)}
            </span>
            <span className="importe block text-[10px] text-armazon-400">
              {c.proximaCuota != null ? enDolares(c.proximaCuota) : ''}
            </span>
          </span>
        ) : (
          <span className="text-[11px] text-armazon-300">—</span>
        ),
    },
    {
      clave: 'situacion',
      encabezado: 'Situación',
      alinear: 'centro',
      contenido: (c) => <Etiqueta situacion={c.situacion} />,
    },
    {
      clave: 'ver',
      encabezado: '',
      alinear: 'derecha',
      contenido: (c) => (
        <BotonDeFila icono={Eye} titulo="Ver el convenio" alPulsar={() => setAbierto(c.id)} />
      ),
    },
  ];

  const r = resumen.data;

  return (
    <>
      <EncabezadoDePagina
        icono={Handshake}
        titulo="Convenios de pago"
        subtitulo="La deuda pactada por cuotas: lo que falta, lo vencido y lo que viene"
        acciones={
          puede('convenios.gestionar') && (
            <button
              type="button"
              onClick={() => setRegistrando(true)}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white shadow-sm transition-colors hover:bg-marca-700"
            >
              <Plus className="size-4" aria-hidden />
              Nuevo convenio
            </button>
          )
        }
      />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Cifra
          rotulo="Por aprobar"
          valor={r ? String(r.porAprobar ?? 0) : '—'}
          {...(r && (r.porAprobar ?? 0) > 0 ? { tono: 'aviso' as const } : {})}
        />
        <Cifra rotulo="Convenios vivos" valor={r ? String(r.vigentes) : '—'} />
        <Cifra
          rotulo="Atrasados"
          valor={r ? String(r.atrasados) : '—'}
          {...(r && r.atrasados > 0 ? { tono: 'aviso' as const } : {})}
        />
        <Cifra rotulo="Por cobrar" valor={r ? enDolares(r.pendiente) : '—'} tono="destacado" />
        <Cifra
          rotulo="Ya vencido"
          valor={r ? enDolares(r.vencido) : '—'}
          {...(r && r.vencido > 0 ? { tono: 'aviso' as const } : {})}
        />
      </div>

      <div className="mb-4 flex gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-sky-600" aria-hidden />
        <p className="text-justify text-xs uppercase leading-relaxed text-sky-800">
          Los registra la Unidad de Coactivas y los aprueba Tesorería o la Jefatura Financiera.{' '}
          <strong>Sus cuotas todavía no se cobran en caja</strong>: llegará cuando EPAGAL entregue
          las facturas de cada convenio. Hasta entonces, un convenio incumplido se marca a mano.
        </p>
      </div>

      <div className="mb-3 flex flex-wrap gap-1.5">
        {FILTROS.map((f) => (
          <button
            key={f.valor}
            type="button"
            onClick={() => {
              setSituacion(f.valor);
              setPagina(0);
            }}
            aria-pressed={situacion === f.valor}
            className={`rounded-lg border px-3 py-1.5 text-[11px] font-semibold uppercase transition-colors ${
              situacion === f.valor
                ? 'border-marca-600 bg-marca-600 text-white'
                : 'border-armazon-200 bg-white text-armazon-600 hover:border-marca-400'
            }`}
          >
            {f.rotulo}
          </button>
        ))}
      </div>

      <Tabla
        datos={convenios.data}
        cargando={convenios.isPending}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(n) => {
          setTamanio(n);
          setPagina(0);
        }}
        entidad="convenios"
        iconoVacio={Handshake}
        mensajeVacio="Ningún convenio con ese filtro"
        herramientas={
          <CampoDeBusqueda
            valor={texto}
            alCambiar={(v) => {
              setTexto(v);
              setPagina(0);
            }}
            marcador="Contribuyente, RUC o referencia…"
            className="w-72"
          />
        }
      />

      {abierto !== null && <PanelDelConvenio id={abierto} alCerrar={() => setAbierto(null)} />}
      {registrando && (
        <ModalDeConvenio
          alCerrar={() => setRegistrando(false)}
          alRegistrar={(c) => {
            setRegistrando(false);
            setAbierto(c.id);
          }}
        />
      )}
    </>
  );
}

function Cifra({
  rotulo,
  valor,
  tono,
}: {
  rotulo: string;
  valor: string;
  tono?: 'destacado' | 'aviso';
}) {
  const estilo =
    tono === 'destacado'
      ? 'border-marca-200 bg-marca-50 text-marca-800'
      : tono === 'aviso'
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : 'border-armazon-200 bg-white text-armazon-900';
  return (
    <div className={`rounded-2xl border px-5 py-3.5 shadow-sm ${estilo}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{rotulo}</p>
      <p className="importe mt-1 text-xl font-bold">{valor}</p>
    </div>
  );
}
